import type { FeatureConfig } from '~/lib/builder-types';
import type { TechSpecResolver } from './types';

export const paymentsTechSpec: TechSpecResolver = (
  stack,
  featureConfig?: FeatureConfig,
) => {
  const isMongo = stack.database === 'mongodb';
  const model =
    (featureConfig?.options['billing-model'] as string | undefined) ??
    'flat-rate';
  const isUsageBased = model === 'usage-based';
  const isPerSeat = model === 'per-seat';

  return {
    pages: [
      {
        id: 'pricing',
        path: '/pricing',
        label: 'Pricing',
        description:
          'Plan comparison table with feature matrix and upgrade CTAs',
        authRequired: false,
      },
      {
        id: 'billing',
        path: '/billing',
        label: 'Billing overview',
        description: 'Current plan status, usage summary, and quick actions',
        authRequired: true,
      },
      {
        id: 'billing-plans',
        path: '/billing/plans',
        label: 'Change plan',
        description: 'Upgrade, downgrade, or switch billing intervals',
        authRequired: true,
      },
      {
        id: 'billing-invoices',
        path: '/billing/invoices',
        label: 'Invoices',
        description: 'Paginated invoice history with PDF download links',
        authRequired: true,
      },
      {
        id: 'billing-payment-methods',
        path: '/billing/payment-methods',
        label: 'Payment methods',
        description: 'Add, remove, and set default credit cards',
        authRequired: true,
      },
      {
        id: 'checkout-success',
        path: '/checkout/success',
        label: 'Checkout success',
        description:
          'Post-Stripe checkout confirmation with onboarding next steps',
        authRequired: false,
      },
    ],
    uxFlows: [
      {
        id: 'subscribe',
        title: 'User subscribes to a plan',
        steps: [
          'User visits /pricing and clicks "Upgrade"',
          'POST /api/billing/checkout creates a Stripe Checkout Session with plan price ID',
          'User is redirected to Stripe-hosted checkout page',
          'User enters card details and completes payment on Stripe',
          'Stripe sends checkout.session.completed webhook to /api/webhooks/stripe',
          'Webhook handler verifies Stripe-Signature header, upserts subscription in DB',
          'User is redirected to /checkout/success — plan is immediately active',
        ],
      },
      {
        id: 'webhook-handler',
        title: 'Stripe webhook event handling',
        steps: [
          'Stripe POSTs an event to POST /api/webhooks/stripe',
          'Server verifies HMAC signature using STRIPE_WEBHOOK_SECRET',
          'Handler dispatches on event.type: checkout.session.completed → activate, customer.subscription.updated → sync status, customer.subscription.deleted → deactivate, invoice.payment_failed → send dunning email',
          'Subscription row updated atomically — status, current_period_end, plan',
          'If payment fails, user receives email and is downgraded after grace period',
        ],
      },
      {
        id: 'cancel',
        title: 'User cancels subscription',
        steps: [
          'User visits /billing and clicks "Cancel plan"',
          'Confirmation dialog shows access end date (current period end)',
          'POST /api/billing/cancel calls stripe.subscriptions.update({ cancel_at_period_end: true })',
          'DB sets subscription status to "canceling", records cancellation reason',
          'User retains full access until period end date',
          'Stripe fires customer.subscription.deleted at period end',
          'Webhook handler sets status to "inactive", downgrades entitlements',
        ],
      },
      {
        id: 'refund',
        title: 'Admin issues a refund',
        steps: [
          'Admin locates invoice in /admin/billing or via customer support tool',
          'POST /api/billing/refund with invoice ID and optional partial amount',
          'Server calls stripe.refunds.create({ charge, amount? })',
          'Stripe processes refund and fires charge.refunded webhook',
          'Invoice record updated with refunded_at and refund_amount_cents',
          'Customer receives Stripe-generated refund confirmation email',
        ],
      },
      ...(isUsageBased
        ? [
            {
              id: 'usage-report',
              title: 'System reports metered usage',
              steps: [
                'At end of billing period or on each metered action, server calculates consumed units',
                'POST /api/billing/usage reports quantity to Stripe metered billing',
                'Stripe aggregates usage and includes it in the next invoice',
                'User can view current period usage on /billing',
              ],
            },
          ]
        : []),
    ],
    endpoints: [
      {
        id: 'get-plans',
        method: 'GET',
        path: '/api/billing/plans',
        description:
          'Return available plans with features and pricing from Stripe Products',
        response:
          '{ plans: { id, name, price, interval, features: string[] }[] }',
      },
      {
        id: 'get-subscription',
        method: 'GET',
        path: '/api/billing/subscription',
        description: 'Return current user subscription status and plan details',
        response: '{ subscription: Subscription, invoices: Invoice[] }',
      },
      {
        id: 'create-checkout',
        method: 'POST',
        path: '/api/billing/checkout',
        description: 'Create Stripe Checkout Session for a plan',
        requestBody: '{ planId, successUrl, cancelUrl }',
        response: '{ checkoutUrl }',
      },
      {
        id: 'billing-portal',
        method: 'POST',
        path: '/api/billing/portal',
        description:
          'Create Stripe Customer Portal session for self-service management',
        response: '{ portalUrl }',
      },
      {
        id: 'cancel-subscription',
        method: 'POST',
        path: '/api/billing/cancel',
        description: 'Cancel subscription at period end',
        requestBody: '{ reason? }',
        response: '{ cancelAt: string }',
      },
      {
        id: 'refund',
        method: 'POST',
        path: '/api/billing/refund',
        description: 'Issue full or partial refund on an invoice (admin only)',
        requestBody: '{ invoiceId, amountCents? }',
        response: '{ refundId }',
      },
      {
        id: 'stripe-webhook',
        method: 'POST',
        path: '/api/webhooks/stripe',
        description:
          'Handle Stripe webhook events — verify signature, dispatch on event type',
        requestBody: 'Stripe event payload (signed with STRIPE_WEBHOOK_SECRET)',
      },
      ...(isUsageBased
        ? [
            {
              id: 'report-usage',
              method: 'POST' as const,
              path: '/api/billing/usage',
              description: 'Report metered usage quantity to Stripe',
              requestBody: '{ quantity, timestamp }',
            },
          ]
        : []),
    ],
    dbTables: isMongo
      ? [
          {
            id: 'subscriptions',
            name: 'subscriptions',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              { name: 'stripeCustomerId', type: 'string', nullable: false },
              { name: 'stripeSubscriptionId', type: 'string', nullable: true },
              {
                name: 'plan',
                type: 'string',
                nullable: false,
                notes: 'free | pro | enterprise',
              },
              {
                name: 'status',
                type: 'string',
                nullable: false,
                notes: 'active | canceling | inactive | past_due',
              },
              { name: 'currentPeriodEnd', type: 'Date', nullable: true },
              { name: 'canceledAt', type: 'Date', nullable: true },
              { name: 'cancelReason', type: 'string', nullable: true },
              ...(isPerSeat
                ? [{ name: 'seatCount', type: 'number', nullable: false }]
                : []),
              { name: 'createdAt', type: 'Date', nullable: false },
              { name: 'updatedAt', type: 'Date', nullable: false },
            ],
            notes: 'Index stripeCustomerId for fast webhook lookup',
          },
          {
            id: 'invoices',
            name: 'invoices',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              { name: 'stripeInvoiceId', type: 'string', nullable: false },
              {
                name: 'amountCents',
                type: 'number',
                nullable: false,
                notes: 'Store in cents to avoid floating-point issues',
              },
              {
                name: 'status',
                type: 'string',
                nullable: false,
                notes: 'paid | open | void | uncollectible',
              },
              { name: 'paidAt', type: 'Date', nullable: true },
              { name: 'hostedUrl', type: 'string', nullable: true },
              { name: 'pdfUrl', type: 'string', nullable: true },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
          },
          {
            id: 'payment_methods',
            name: 'payment_methods',
            columns: [
              { name: '_id', type: 'ObjectId', nullable: false },
              { name: 'userId', type: 'ObjectId ref users', nullable: false },
              {
                name: 'stripePaymentMethodId',
                type: 'string',
                nullable: false,
              },
              {
                name: 'type',
                type: 'string',
                nullable: false,
                notes: 'card | bank_account | sepa_debit',
              },
              { name: 'last4', type: 'string', nullable: true },
              { name: 'brand', type: 'string', nullable: true },
              { name: 'expMonth', type: 'number', nullable: true },
              { name: 'expYear', type: 'number', nullable: true },
              { name: 'isDefault', type: 'boolean', nullable: false },
              { name: 'createdAt', type: 'Date', nullable: false },
            ],
          },
        ]
      : [
          {
            id: 'subscriptions',
            name: 'subscriptions',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              { name: 'stripe_customer_id', type: 'text', nullable: false },
              { name: 'stripe_subscription_id', type: 'text', nullable: true },
              {
                name: 'plan',
                type: 'text',
                nullable: false,
                notes: 'free | pro | enterprise',
              },
              {
                name: 'status',
                type: 'text',
                nullable: false,
                notes: 'active | canceling | inactive | past_due',
              },
              { name: 'current_period_end', type: 'timestamp', nullable: true },
              { name: 'canceled_at', type: 'timestamp', nullable: true },
              { name: 'cancel_reason', type: 'text', nullable: true },
              ...(isPerSeat
                ? [{ name: 'seat_count', type: 'integer', nullable: false }]
                : []),
              { name: 'created_at', type: 'timestamp', nullable: false },
              { name: 'updated_at', type: 'timestamp', nullable: false },
            ],
            notes:
              'Index stripe_customer_id for webhook lookup. One row per user; upsert on webhook events.',
          },
          {
            id: 'invoices',
            name: 'invoices',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              {
                name: 'stripe_invoice_id',
                type: 'text',
                nullable: false,
                notes: 'Unique; used to deduplicate webhook deliveries',
              },
              {
                name: 'amount_cents',
                type: 'integer',
                nullable: false,
                notes: 'Store in cents — never floats',
              },
              {
                name: 'status',
                type: 'text',
                nullable: false,
                notes: 'paid | open | void | uncollectible',
              },
              { name: 'paid_at', type: 'timestamp', nullable: true },
              { name: 'hosted_url', type: 'text', nullable: true },
              { name: 'pdf_url', type: 'text', nullable: true },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes: 'Index user_id + created_at for invoice list queries',
          },
          {
            id: 'payment_methods',
            name: 'payment_methods',
            columns: [
              { name: 'id', type: 'uuid', nullable: false },
              { name: 'user_id', type: 'uuid FK users', nullable: false },
              {
                name: 'stripe_pm_id',
                type: 'text',
                nullable: false,
                notes: 'Stripe PaymentMethod ID',
              },
              {
                name: 'type',
                type: 'text',
                nullable: false,
                notes: 'card | bank_account | sepa_debit',
              },
              { name: 'last4', type: 'text', nullable: true },
              { name: 'brand', type: 'text', nullable: true },
              { name: 'exp_month', type: 'integer', nullable: true },
              { name: 'exp_year', type: 'integer', nullable: true },
              { name: 'is_default', type: 'boolean', nullable: false },
              { name: 'created_at', type: 'timestamp', nullable: false },
            ],
            notes: 'Only one row per user should have is_default = true',
          },
        ],
  };
};
